/**
 * Procedural gameplay audio. (§11)
 *
 * Everything here is SYNTHESIZED at runtime with the Web Audio API — no audio
 * files, no downloaded samples, no CC0 packs (§16 asset-provenance rule: zero
 * licensing surface). The one pre-existing sound in the game, the menu's
 * `/audio/stadium-intro.mp3` (ui/menu.ts), predates this system and is left
 * alone; this module is the entire gameplay audio layer: crowd bed (rumble +
 * stereo wash/voices + occasional chant "surges"), kicks, bounces, the
 * referee's whistle, the goal swish + roar, and a smooth master duck for
 * `setPaused()` (§11 "mute the audio when a player pauses").
 *
 * A YouTube stadium-ambience clip was used only as a REFERENCE for how the
 * crowd should feel (deep rumble, irregular voice swells, occasional chant
 * surges, wide/diffuse) — nothing from it is sampled or downloaded; every
 * sound below is synthesized at runtime, same as everything else here.
 *
 * ── Two layers, split so the interesting part is testable (§ project rule:
 *    "the Web Audio graph itself can't run in vitest — no AudioContext in
 *    node") ──────────────────────────────────────────────────────────────────
 *
 *   1. DECISION (pure, exported, unit-tested): `decideAudioFrame` takes the
 *      ball's position/velocity, the match phase and this tick's rule events,
 *      and a small mutable state bag, and fills a mutable `AudioTriggers`
 *      struct with what should fire this tick and how loud/high/long. No
 *      Web Audio, no wall clock — a pure function of its inputs, exactly like
 *      the sim's own rule functions, so it can be pinned with plain
 *      `expect()` assertions. Its ONE source of non-determinism, an injected
 *      `rng` parameter (defaults to `Math.random`) used solely to schedule
 *      crowd "surge" events, is itself an explicit input — a test can swap in
 *      a deterministic sequence to pin the scheduler exactly.
 *   2. SYNTHESIS (`SynthEngine`, internal): turns a decided trigger into
 *      actual oscillators/noise/filters on a lazily-created `AudioContext`.
 *      Never throws — every method is defensive, because a muted or
 *      unavailable AudioContext (autoplay policy, a browser without one, the
 *      node test runner) must never take the game loop down with it.
 *
 * ── The seam (mirrors ui/banners.ts + render/vfx.ts) ─────────────────────────
 *
 * `createAudioSystem().update(input, events)` is called once per SIM TICK
 * (not once per rendered frame) from inside each mount's `driver.advance`
 * callback, right after `step()` — the same placement banners.ts's
 * `pushEvents` and testArena's `updateMeasure` already use. That matters
 * because `StepResult.events` is drained every tick (step.ts zeroes it at the
 * top of `step()`): reading it only after `driver.advance` returns would lose
 * every event but the last tick's in a frame that ran more than one tick.
 * `events` may be `NO_AUDIO_EVENTS` (a frozen empty array — the online path
 * has no local RuleEvents; the server owns rules, same limitation banners.ts
 * documents) — the whistle then falls back to the phase-transition edge
 * detector below, exactly like banners' "Feed 2".
 *
 * ── Zero-alloc steady path (§15.7) ──────────────────────────────────────────
 *
 * `AudioDecisionState` and the output `AudioTriggers` are allocated once by
 * `createAudioSystem()` and mutated in place every call. One-shot Web Audio
 * nodes (a kick, a whistle blast) are allocated per trigger — unavoidable with
 * Web Audio and explicitly fine (they are GC'd once `onended` fires); the
 * continuous crowd bed is built once and only its gain is touched per tick.
 */

import { MatchPhase, BALL_RADIUS, type RuleEventType } from '@fg/shared/sim';

// ---------------------------------------------------------------------------
// Public shape
// ---------------------------------------------------------------------------

/** Structural — `RuleEvent` satisfies this; so does any `{ type }` stub.
 *
 *  `phase` is optional and only ever read for a `WHISTLE` event, where the sim
 *  sets it to the restart being signalled (types.ts) so this module can pick a
 *  pattern — a long blast to start a half, a short one for a throw-in. Every
 *  other event type leaves it undefined and the field is ignored. */
export interface GameEventLike {
  readonly type: RuleEventType;
  readonly phase?: MatchPhase;
}

/** Shared empty array for callers with no local rule events (online, §above). */
export const NO_AUDIO_EVENTS: readonly GameEventLike[] = [];

/** One tick's worth of state the decision layer needs. Ball VELOCITY, not a
 *  position delta the decision layer would have to derive — every call site
 *  (practice/solo/arena's own WorldState, and online's `prediction.world`)
 *  already has the sim's real `BallState.vx/vy/vz`, which is exact where a
 *  render-frame position delta would only be an approximation. */
export interface AudioTickInput {
  /** Seconds since the previous `update()` call. All shipped call sites pass
   *  the fixed sim step (FIXED_DT), so envelopes/timers below are tick-exact. */
  dt: number;
  ballX: number;
  ballY: number;
  ballZ: number;
  ballVX: number;
  ballVY: number;
  ballVZ: number;
  phase: MatchPhase;
  /** Half the pitch length, m — defines the "attacking third" boundary. */
  pitchHalfLength: number;
}

export interface AudioSystemOptions {
  /** Ambient crowd bed on/off. Default true. The Test Arena directive is
   *  explicit: "arena can skip crowd bed — keep whistle/kick." */
  crowd?: boolean;
}

export interface AudioSystem {
  update(input: AudioTickInput, events: readonly GameEventLike[]): void;
  setMasterVolume(v: number): void;
  /** Smoothly ducks (true) or restores (false) the ENTIRE output — crowd bed
   *  and every one-shot — over ~120ms / ~250ms respectively; never a hard
   *  cut/click (§11 "mute the audio when a player pauses"). Idempotent: safe
   *  to call with the same value repeatedly. */
  setPaused(paused: boolean): void;
  /** Must be called from a real user gesture (click/keydown) — browsers block
   *  autoplay. Safe to call repeatedly; a no-op once already running. */
  resume(): void;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Decision layer — pure, exported, unit-tested (audio.test.ts).
// ---------------------------------------------------------------------------

export type WhistlePattern = 'none' | 'short' | 'long' | 'triple';

export interface AudioTriggers {
  kick: boolean;
  /** 0..1 normalized strike strength — scales the kick's gain/pitch. */
  kickStrength: number;
  /** Delta > KICK_POWERFUL_DELTA_MPS — the longer, lower flavor (§ spec). */
  kickPowerful: boolean;
  bounce: boolean;
  /** 0..1 normalized bounce strength. */
  bounceStrength: number;
  /** Net "swish" one-shot — fires alongside a GOAL event. */
  goalNetSwish: boolean;
  /** True the tick the goal roar envelope (re)starts (informational — the
   *  envelope itself lives in `crowdBedGain`, already rolled in). */
  crowdGoalRoar: boolean;
  /** Disappointed "ohh" one-shot — a shot went unrewarded (see the heuristic
   *  in `decideAudioFrame`). */
  crowdOhh: boolean;
  whistle: WhistlePattern;
  /** Final 0..1 target gain for the continuous crowd bed this tick. */
  crowdBedGain: number;
  /** True the tick a "surge" (localized chant/reaction swell) should start —
   *  informational trigger for the synthesis layer, which owns the actual
   *  bandpass-swept one-shot (see SynthEngine.playCrowdSurge). */
  crowdSurge: boolean;
  /** 2..4s — how long THIS surge instance should run; only meaningful the
   *  tick `crowdSurge` is true. */
  crowdSurgeDurationS: number;
}

function createAudioTriggers(): AudioTriggers {
  return {
    kick: false,
    kickStrength: 0,
    kickPowerful: false,
    bounce: false,
    bounceStrength: 0,
    goalNetSwish: false,
    crowdGoalRoar: false,
    crowdOhh: false,
    whistle: 'none',
    crowdBedGain: 0,
    crowdSurge: false,
    crowdSurgeDurationS: 0,
  };
}

interface CrowdEnvelope {
  /** Smoothed toward the phase-driven base target (quiet/live). */
  baseGain: number;
  /** Smoothed toward the attacking-third/shot-pending bonus. */
  swellGain: number;
  roarActive: boolean;
  /** Seconds since the roar envelope (re)started. */
  roarElapsed: number;
  /** Seconds the ball has been continuously in the attacking third during
   *  LIVE play — ramps the slow "sustained possession near the box" bonus
   *  and decays (faster than it rises) the instant that stops being true,
   *  including every restart-staging phase (§ crowd engineering pass). */
  possessionS: number;
}

export interface AudioDecisionState {
  seeded: boolean;
  prevBallSpeed: number;
  prevBallVY: number;
  lastPhase: MatchPhase;
  /** Seconds remaining in the "did that shot score" window; 0 = not pending. */
  shotPendingS: number;
  /** Countdown to the next "surge" attempt, seconds; re-armed with a fresh
   *  random draw once a surge actually fires. Stays <= 0 (checked again next
   *  tick) while suppressed, so a surge due during a roar/groan is postponed,
   *  never dropped. */
  surgeNextInS: number;
  /** Seconds left in the "a groan one-shot is still playing" guard window —
   *  see GROAN_GUARD_S. */
  groanGuardS: number;
  /** Seconds left during which an authoritative WHISTLE event suppresses the
   *  phase-transition whistle fallback — see WHISTLE_EVENT_GUARD_S. */
  whistleGuardS: number;
  crowd: CrowdEnvelope;
}

export function createAudioDecisionState(): AudioDecisionState {
  return {
    seeded: false,
    prevBallSpeed: 0,
    prevBallVY: 0,
    lastPhase: MatchPhase.Warmup,
    shotPendingS: 0,
    surgeNextInS: 0, // real random value drawn on the first (seed) call
    groanGuardS: 0,
    whistleGuardS: 0,
    crowd: { baseGain: 0, swellGain: 0, roarActive: false, roarElapsed: 0, possessionS: 0 },
  };
}

// ── Tunables (cosmetic/presentation only — never read by /sim, so these stay
//    local rather than in shared/constants.ts, same convention as vfx.ts's
//    TRAIL_/SPRAY_/RAIN_ constants). ──────────────────────────────────────────

/** Ball-speed rising edge that reads as "a kick just happened", m/s. */
const KICK_MIN_DELTA_MPS = 8;
/** Above this delta, the strike gets the longer/lower "powerful" flavor. */
const KICK_POWERFUL_DELTA_MPS = 20;
/** Delta that maps to full-strength (1.0) kick gain/pitch. */
const KICK_NORM_DELTA_MPS = 32;

/** "Near the ground" for bounce detection — a few ball radii of margin. */
const BOUNCE_MAX_HEIGHT_M = BALL_RADIUS * 4;
const BOUNCE_MIN_SPEED_MPS = 2;
const BOUNCE_NORM_SPEED_MPS = 8;

/** Ball inside this fraction of a half-pitch-length from either goal line
 *  counts as "in the attacking third" for crowd swell + the shot heuristic. */
const ATTACKING_THIRD_FRAC = 1 / 3;
/** SHOT event heuristic window (§ spec: "no GOAL within ~1.5s"). */
const SHOT_PENDING_WINDOW_S = 1.5;

const CROWD_BASE_QUIET = 0.22;
const CROWD_BASE_LIVE = 0.5;
/** 1/s — how fast the base bed chases its phase-driven target. */
const CROWD_BASE_RATE = 1.2;
const CROWD_SWELL_BONUS = 0.28;
const CROWD_SWELL_ATTACK_RATE = 2.5;
const CROWD_SWELL_RELEASE_RATE = 0.8;

const ROAR_ATTACK_S = 0.4;
const ROAR_HOLD_S = 2.0;
const ROAR_DECAY_S = 1.5;
const ROAR_TOTAL_S = ROAR_ATTACK_S + ROAR_HOLD_S + ROAR_DECAY_S; // ~2-3s swell + decay
const ROAR_PEAK_GAIN = 0.45;

/** Sustained-possession crowd bonus — distinct from `swellGain` above (which
 *  attacks/releases in under a second): this ramps in slowly over several
 *  seconds of CONTINUOUS presence in the attacking third during live play
 *  only, and decays (faster than it rises) the moment that stops — including
 *  every restart-staging phase, so the bed drops for a throw-in/free-kick
 *  lull even if the ball is technically still sitting deep. */
const POSSESSION_RAMP_S = 6;
const POSSESSION_BONUS_MAX = 0.12;
const POSSESSION_DECAY_RATE = 1.5; // 1/s

/** Crowd "surge" (chant/reaction) scheduling. The SCHEDULE (when/how long)
 *  is decided here, pure and testable with an injected `rng`; the synthesis
 *  layer (SynthEngine.playCrowdSurge) owns the actual bandpass-swept sound. */
const SURGE_MIN_INTERVAL_S = 8;
const SURGE_MAX_INTERVAL_S = 20;
const SURGE_MIN_DURATION_S = 2;
const SURGE_MAX_DURATION_S = 4;
/**
 * How long an authoritative `WHISTLE` event suppresses the PHASE-transition
 * whistle fallback below. (§5.6)
 *
 * Both feeds exist on purpose (mirroring banners.ts's two feeds): offline we have
 * real rule events, online — before the RefereeSignal wire frame landed — there
 * was only `match.phase`. Now that the sim's own WHISTLE fires on the restart
 * Setup→Ready flip, the Kickoff→InPlay phase edge a few ticks later would blow a
 * SECOND long blast for the same kickoff. So a real whistle event wins and mutes
 * the inferred one for a moment. Comfortably longer than the Ready window
 * (RESTART_READY_S) that separates the two, short enough that a genuine half-time
 * whistle seconds later is never eaten.
 */
const WHISTLE_EVENT_GUARD_S = 2.5;

/** How long a "groan" (crowdOhh) one-shot is treated as still playing, so a
 *  surge doesn't start on top of it — mirrors playOhh()'s own ~1.0s envelope
 *  in the synthesis layer (§ judgment call: there is no explicit groan
 *  envelope in the decision state the way `roarActive` models the roar, so
 *  this fixed guard window stands in for one). */
const GROAN_GUARD_S = 1.0;

/**
 * Whistle pattern for the sim's `WHISTLE` event, by the restart it signals.
 * (§5.1/§5.6; owner: *"the ball is in game only after refree whisely for start
 * off , throwin , foul , corner"*.)
 *
 * A referee does not blow the same blast for everything, and the pattern is the
 * only way a player can tell what just happened without reading the HUD:
 *   - KICKOFF gets the LONG blast that starts a half — the ceremonial one.
 *   - a PENALTY gets a long blast too: it is the one restart where the whole
 *     stadium is waiting on the referee, and a curt peep would undersell it.
 *   - everything else (throw-in, corner, goal kick, free kick, drop ball) gets a
 *     single SHORT blast, which is what those actually sound like.
 *
 * Pure and exported so audio.test.ts can pin every phase without an AudioContext.
 */
export function whistlePatternForRestart(phase: MatchPhase | undefined): WhistlePattern {
  if (phase === MatchPhase.Kickoff || phase === MatchPhase.Penalty) return 'long';
  return 'short';
}

/** Exported so audio.test.ts can pin the master-volume clamp directly — the
 *  synthesis layer that actually applies it needs a real AudioContext, which
 *  vitest's node environment does not have (see the header comment). */
export function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Target gain + ramp duration for `setPaused()`'s master duck (§11 "mute the
 *  audio when a player pauses") — pure, exactly like `clamp01` above and for
 *  the same reason: `SynthEngine.setPaused` (the thing that actually applies
 *  this to a `GainNode`) needs a real AudioContext, which vitest's node
 *  environment does not have. Paused targets silence over ~120ms; resumed
 *  restores full over ~250ms — always a ramp, never a hard cut. */
export interface DuckTarget {
  gain: number;
  rampS: number;
}
export function pauseDuckTarget(paused: boolean): DuckTarget {
  return paused ? { gain: DUCK_SILENT, rampS: PAUSE_DUCK_S } : { gain: DUCK_FULL, rampS: PAUSE_RESTORE_S };
}

/** Exponential-ish approach: reaches ~63% of the remaining distance per 1/rate
 *  seconds. Used for every smoothed gain below — cheap, stable, no overshoot. */
function approach(current: number, target: number, dt: number, rate: number): number {
  const k = Math.min(1, rate * dt);
  return current + (target - current) * k;
}

function isLivePhase(phase: MatchPhase): boolean {
  return phase === MatchPhase.InPlay || phase === MatchPhase.ExtraTime || phase === MatchPhase.PenaltyShootout;
}

/** Roar envelope value (0..ROAR_PEAK_GAIN) at `t` seconds since it started. */
function roarEnvelope(t: number): number {
  if (t < ROAR_ATTACK_S) return (t / ROAR_ATTACK_S) * ROAR_PEAK_GAIN;
  if (t < ROAR_ATTACK_S + ROAR_HOLD_S) return ROAR_PEAK_GAIN;
  const decayT = t - ROAR_ATTACK_S - ROAR_HOLD_S;
  return ROAR_PEAK_GAIN * Math.max(0, 1 - decayT / ROAR_DECAY_S);
}

/**
 * Pure decision function. Mutates `state` (derivatives for the next call) and
 * `out` (this tick's triggers) in place — zero allocation. Exported for
 * `audio.test.ts`; `createAudioSystem` is the only production caller.
 *
 * Heuristics are DELIBERATELY simple, per the brief ("keep the heuristic
 * simple: use ball speed + goal events"):
 *   - a KICK is a ball-speed rising edge > KICK_MIN_DELTA_MPS in one call.
 *   - a "SHOT" is a powerful kick while the ball is in an attacking third —
 *     this does not know direction/target, so a hard clearance out of your
 *     own defensive third also (harmlessly) arms the miss-groan window; a
 *     documented judgment call, never a gameplay effect.
 *   - the shot resolves to a roar if a GOAL event lands inside the window, or
 *     an "ohh" once the window closes without one.
 *
 * `rng` defaults to `Math.random` and is the ONLY source of non-determinism
 * in this otherwise-pure function — it exists solely to schedule "surge"
 * (crowd chant/reaction) events (§ crowd engineering pass) and is injectable
 * so audio.test.ts can pin the scheduler's bounds and suppression rule with
 * a deterministic sequence instead of a real RNG.
 */
export function decideAudioFrame(
  state: AudioDecisionState,
  out: AudioTriggers,
  input: AudioTickInput,
  events: readonly GameEventLike[],
  rng: () => number = Math.random,
): void {
  out.kick = false;
  out.kickStrength = 0;
  out.kickPowerful = false;
  out.bounce = false;
  out.bounceStrength = 0;
  out.goalNetSwish = false;
  out.crowdGoalRoar = false;
  out.crowdOhh = false;
  out.whistle = 'none';
  out.crowdSurge = false;
  out.crowdSurgeDurationS = 0;

  const dt = Math.max(1e-4, input.dt);
  const ballSpeed = Math.hypot(input.ballVX, input.ballVY, input.ballVZ);

  if (!state.seeded) {
    // Never trigger a kick/bounce from the first call's 0-baseline jump.
    state.seeded = true;
    state.prevBallSpeed = ballSpeed;
    state.prevBallVY = input.ballVY;
    state.lastPhase = input.phase;
    state.surgeNextInS = SURGE_MIN_INTERVAL_S + rng() * (SURGE_MAX_INTERVAL_S - SURGE_MIN_INTERVAL_S);
    out.crowdBedGain = state.crowd.baseGain;
    return;
  }

  // ── kick edge ─────────────────────────────────────────────────────────
  const speedDelta = ballSpeed - state.prevBallSpeed;
  if (speedDelta > KICK_MIN_DELTA_MPS) {
    out.kick = true;
    out.kickStrength = clamp01(
      (speedDelta - KICK_MIN_DELTA_MPS) / (KICK_NORM_DELTA_MPS - KICK_MIN_DELTA_MPS),
    );
    out.kickPowerful = speedDelta > KICK_POWERFUL_DELTA_MPS;
  }

  // ── bounce: vy flips negative → positive near the ground ───────────────
  const nearGround = input.ballY < BOUNCE_MAX_HEIGHT_M;
  if (nearGround && state.prevBallVY < -BOUNCE_MIN_SPEED_MPS && input.ballVY >= 0) {
    out.bounce = true;
    out.bounceStrength = clamp01(
      (-state.prevBallVY - BOUNCE_MIN_SPEED_MPS) / BOUNCE_NORM_SPEED_MPS,
    );
  }

  // ── shot heuristic + goal roar/miss-groan ───────────────────────────────
  const inAttackingThird = Math.abs(input.ballX) > input.pitchHalfLength * (1 - ATTACKING_THIRD_FRAC);
  let sawGoal = false;
  let sawFoul = false;
  let sawHalfTime = false;
  let sawFullTime = false;
  let sawWhistle = false;
  let whistlePhase: MatchPhase | undefined;
  for (const e of events) {
    if (e.type === 'GOAL') sawGoal = true;
    else if (e.type === 'FOUL') sawFoul = true;
    else if (e.type === 'HALF_TIME') sawHalfTime = true;
    else if (e.type === 'FULL_TIME') sawFullTime = true;
    else if (e.type === 'WHISTLE') {
      sawWhistle = true;
      whistlePhase = e.phase;
    }
  }

  if (sawGoal) {
    out.goalNetSwish = true;
    out.crowdGoalRoar = true;
    state.crowd.roarActive = true;
    state.crowd.roarElapsed = 0;
    state.shotPendingS = 0; // resolved — no miss-groan right after a goal
  } else if (out.kick && out.kickPowerful && inAttackingThird) {
    state.shotPendingS = SHOT_PENDING_WINDOW_S; // (re)arm the "did it go in" window
  } else if (state.shotPendingS > 0) {
    state.shotPendingS = Math.max(0, state.shotPendingS - dt);
    if (state.shotPendingS === 0) out.crowdOhh = true; // window closed, no goal
  }

  // ── whistle: events first (edge-triggered), then the phase-transition
  //    fallback (mirrors banners.ts's dual-feed — online has phase only). ───
  //
  // THE RESTART WHISTLE (§5.6). `WHISTLE` is the referee's actual signal: the sim
  // raises it on every staged restart's Setup→Ready flip, one tick before the
  // earliest legal strike (rules/fsm.ts). It is listed FIRST so half/full time
  // still override it — those are the bigger announcements and can coincide.
  //
  // Before this, a whistle could only be INFERRED from a Kickoff→InPlay phase
  // edge, which fires when the ball is actually struck (a Ready window late) and
  // never fired at all for a throw-in, corner, goal kick, free kick or penalty.
  // That is the owner's complaint, and this line is the fix.
  if (sawWhistle) {
    out.whistle = whistlePatternForRestart(whistlePhase);
    state.whistleGuardS = WHISTLE_EVENT_GUARD_S;
  }
  if (sawFoul) out.whistle = 'short';
  if (sawHalfTime) out.whistle = 'long';
  if (sawFullTime) out.whistle = 'triple';

  const phase = input.phase;
  // The fallback is now a FALLBACK: with a real WHISTLE event in hand, inferring
  // a second one off the phase edge a few ticks later would double-blow the same
  // kickoff. Suppressed for WHISTLE_EVENT_GUARD_S; still the only feed for a
  // caller with no events at all.
  if (out.whistle === 'none' && state.whistleGuardS <= 0 && phase !== state.lastPhase) {
    if (phase === MatchPhase.HalfTime) out.whistle = 'long';
    else if (phase === MatchPhase.FullTime) out.whistle = 'triple';
    // Kickoff → InPlay: the ref blows to start play (opening kickoff AND the
    // restart after half-time both pass through Kickoff first).
    else if (state.lastPhase === MatchPhase.Kickoff && phase === MatchPhase.InPlay) out.whistle = 'long';
  }
  if (!sawWhistle && state.whistleGuardS > 0) {
    state.whistleGuardS = Math.max(0, state.whistleGuardS - dt);
  }

  // ── crowd bed continuous gain ────────────────────────────────────────────
  const baseTarget = isLivePhase(phase) ? CROWD_BASE_LIVE : CROWD_BASE_QUIET;
  state.crowd.baseGain = approach(state.crowd.baseGain, baseTarget, dt, CROWD_BASE_RATE);

  const swellTarget = inAttackingThird || state.shotPendingS > 0 ? CROWD_SWELL_BONUS : 0;
  const swellRate = swellTarget > state.crowd.swellGain ? CROWD_SWELL_ATTACK_RATE : CROWD_SWELL_RELEASE_RATE;
  state.crowd.swellGain = approach(state.crowd.swellGain, swellTarget, dt, swellRate);

  // Sustained possession near the box — slow ramp, LIVE play only; a
  // restart-staging phase (FreeKick/ThrowIn/Corner/...) decays it even if
  // the ball is still sitting deep, per the brief's "drop it during restart
  // staging lulls".
  const sustainedPossession = isLivePhase(phase) && inAttackingThird;
  if (sustainedPossession) {
    state.crowd.possessionS = Math.min(POSSESSION_RAMP_S, state.crowd.possessionS + dt);
  } else {
    state.crowd.possessionS = Math.max(0, state.crowd.possessionS - dt * POSSESSION_DECAY_RATE);
  }
  // Explicitly clamped 0..POSSESSION_BONUS_MAX — possessionS is itself capped
  // at POSSESSION_RAMP_S above, so this can never grow past the max no matter
  // how long possession is sustained (pinned in audio.test.ts).
  const possessionBonus = clamp01(state.crowd.possessionS / POSSESSION_RAMP_S) * POSSESSION_BONUS_MAX;

  if (state.crowd.roarActive) {
    state.crowd.roarElapsed += dt;
    if (state.crowd.roarElapsed > ROAR_TOTAL_S) state.crowd.roarActive = false;
  }
  const roarGain = state.crowd.roarActive ? roarEnvelope(state.crowd.roarElapsed) : 0;

  out.crowdBedGain = clamp01(state.crowd.baseGain + state.crowd.swellGain + possessionBonus + roarGain);

  // ── occasional crowd "surge" (chant/reaction) scheduling — postponed
  //    (never dropped) while a roar or groan is (about to be) playing ──────
  if (out.crowdOhh) state.groanGuardS = GROAN_GUARD_S;
  else if (state.groanGuardS > 0) state.groanGuardS = Math.max(0, state.groanGuardS - dt);

  state.surgeNextInS -= dt;
  const surgeSuppressed = state.crowd.roarActive || state.groanGuardS > 0;
  if (state.surgeNextInS <= 0 && !surgeSuppressed) {
    out.crowdSurge = true;
    out.crowdSurgeDurationS = SURGE_MIN_DURATION_S + rng() * (SURGE_MAX_DURATION_S - SURGE_MIN_DURATION_S);
    state.surgeNextInS = SURGE_MIN_INTERVAL_S + rng() * (SURGE_MAX_INTERVAL_S - SURGE_MIN_INTERVAL_S);
  }

  // ── commit derivatives for the next call ────────────────────────────────
  state.prevBallSpeed = ballSpeed;
  state.prevBallVY = input.ballVY;
  state.lastPhase = phase;
}

// ---------------------------------------------------------------------------
// Synthesis layer — Web Audio. Never throws; every entry point is a no-op
// (not a crash) when AudioContext is unavailable, suspended-forever, or the
// browser refuses a node for some exotic reason.
// ---------------------------------------------------------------------------

interface NoiseBuffers {
  /** Wash (lowpassed) and voice (bandpassed) layers each get TWO decorrelated
   *  buffers — one per stereo side, panned ±0.4 for width (§ crowd pass). */
  washL: AudioBuffer;
  washR: AudioBuffer;
  voiceL: AudioBuffer;
  voiceR: AudioBuffer;
  /** Deep, near-mono "never fully settles" layer under the wash. */
  rumble: AudioBuffer;
  /** Localized chant/reaction swell one-shot source (looped under a bandpass
   *  sweep for its 2-4s duration — see playCrowdSurge). */
  surge: AudioBuffer;
  click: AudioBuffer;
  long: AudioBuffer;
}

function getAudioContextCtor(): (new () => AudioContext) | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as { AudioContext?: new () => AudioContext; webkitAudioContext?: new () => AudioContext };
  return w.AudioContext ?? w.webkitAudioContext;
}

/** Master output duck (pause mute, §11 "mute the audio when a player
 *  pauses") — smooth ramps, never a hard cut, so a pause/resume never clicks. */
const PAUSE_DUCK_S = 0.12;
const PAUSE_RESTORE_S = 0.25;
/** linearRampToValueAtTime can target exactly 0 (unlike the exponential ramps
 *  used elsewhere in this file for one-shots, which can't target 0). */
const DUCK_SILENT = 0;
const DUCK_FULL = 1;

/** Continuous organic "wobble" on the whole crowd bus — two incommensurate
 *  LFOs (built into the graph, see ensureCrowd) plus this JS-driven slow
 *  random walk, recombined every tick so the bed never reads as a loop. */
const WOBBLE_MIN = 0.82;
const WOBBLE_MAX = 1.18;
const WOBBLE_WALK_RATE = 0.12; // per-second step scale
const WOBBLE_REVERT_RATE = 0.06; // 1/s pull back toward 1.0 (keeps it bounded)

/** Crowd-bus gain budget — kept equal to the PRE-existing single-mono-wash-
 *  plus-voices total (0.85 + 0.32 = 1.17) so adding the rumble layer and
 *  splitting wash/voices into stereo pairs re-balances rather than stacks
 *  louder (§ brief: "keep total gain budget the same"). */
const RUMBLE_GAIN = 0.35;
const WASH_GAIN_EACH = 0.28; // × 2 sides = 0.56
const VOICE_GAIN_EACH = 0.13; // × 2 sides = 0.26
// 0.35 + 0.56 + 0.26 === 1.17

const CROWD_SHELF_HZ = 6000;
const CROWD_SHELF_DB = -6;
const SURGE_PEAK_GAIN = 0.3;

class SynthEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private masterVolume = 1;
  private noise: NoiseBuffers | null = null;

  /** Ducks the ENTIRE output (crowd + one-shots) to silence on pause — sits
   *  between `masterGain` (the user's volume slider) and `ctx.destination`
   *  so setMasterVolume() and setPaused() never fight over the same node. */
  private duckGain: GainNode | null = null;
  private paused = false;

  private crowdBuilt = false;
  private crowdBedGainNode: GainNode | null = null;
  /** Base 1.0, wandered by the two crowd LFOs + crowdTick()'s random walk. */
  private crowdWobbleGainNode: GainNode | null = null;
  private wobbleValue = 1.0;
  private readonly crowdNodes: Array<AudioNode & { stop?: (when?: number) => void }> = [];

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const Ctor = getAudioContextCtor();
      if (!Ctor) return null;
      const ctx = new Ctor();
      const gain = ctx.createGain();
      gain.gain.value = this.masterVolume;
      const duck = ctx.createGain();
      duck.gain.value = this.paused ? DUCK_SILENT : DUCK_FULL;
      gain.connect(duck).connect(ctx.destination);
      this.ctx = ctx;
      this.masterGain = gain;
      this.duckGain = duck;
      return ctx;
    } catch {
      return null;
    }
  }

  private now(): number {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  private buildNoise(ctx: AudioContext): NoiseBuffers {
    const mk = (seconds: number): AudioBuffer => {
      const buf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * seconds)), ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      return buf;
    };
    // Every layer gets its own buffer length so no two loop points ever line
    // up (avoids an audible phase-lock between noise sources).
    return {
      washL: mk(4.0), washR: mk(4.53),
      voiceL: mk(4.37), voiceR: mk(4.91),
      rumble: mk(5.3),
      surge: mk(2.9),
      click: mk(0.08),
      long: mk(1.2),
    };
  }

  resume(): void {
    try {
      const ctx = this.ensureContext();
      if (!ctx) return;
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    } catch {
      /* a blocked/failed resume must never reach the game loop */
    }
  }

  setMasterVolume(v: number): void {
    this.masterVolume = clamp01(v);
    try {
      if (this.masterGain) this.masterGain.gain.setTargetAtTime(this.masterVolume, this.now(), 0.05);
    } catch {
      /* ignore */
    }
  }

  /** Smoothly ducks (paused) or restores (resumed) the ENTIRE output — crowd
   *  bed + every one-shot, since this sits downstream of all of them at
   *  `duckGain`. Always a ramp, never a hard cut (§11 "mute the audio when a
   *  player pauses" — a click on pause/resume would be worse than the noise
   *  it's hiding). Safe to call before any sound has ever played: it just
   *  seeds `paused` for when ensureContext() first builds the graph. */
  setPaused(paused: boolean): void {
    this.paused = paused;
    try {
      const ctx = this.ensureContext();
      if (!ctx || !this.duckGain) return;
      const now = this.now();
      const { gain, rampS } = pauseDuckTarget(paused);
      const g = this.duckGain.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(gain, now + rampS);
    } catch {
      /* ignore */
    }
  }

  /** Called once per tick (from createAudioSystem.update, same cadence as
   *  setCrowdBedGain) to advance the crowd bus's organic "wobble" — a bounded
   *  random walk, mean-reverting so it can never run away, summed (via the
   *  AudioParam automation this writes PLUS the two LFOs already connected to
   *  the same gain in ensureCrowd) with the audio-rate incommensurate LFOs. */
  crowdTick(dt: number): void {
    if (!this.crowdBuilt || !this.crowdWobbleGainNode || !this.ctx) return;
    try {
      this.wobbleValue += (Math.random() - 0.5) * WOBBLE_WALK_RATE * dt;
      this.wobbleValue += (1 - this.wobbleValue) * WOBBLE_REVERT_RATE * dt;
      if (this.wobbleValue < WOBBLE_MIN) this.wobbleValue = WOBBLE_MIN;
      if (this.wobbleValue > WOBBLE_MAX) this.wobbleValue = WOBBLE_MAX;
      this.crowdWobbleGainNode.gain.setTargetAtTime(this.wobbleValue, this.now(), 0.25);
    } catch {
      /* ignore */
    }
  }

  private ensureCrowd(): void {
    if (this.crowdBuilt) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.masterGain) return;
    try {
      this.noise ??= this.buildNoise(ctx);
      const bed = ctx.createGain();
      bed.gain.value = 0; // driven per-tick by setCrowdBedGain

      // Organic wobble sits downstream of `bed` — base 1.0, wandered by the
      // two incommensurate LFOs below (built here) and crowdTick()'s slow JS
      // random walk, so the excitement level itself never sounds looped.
      const wobble = ctx.createGain();
      wobble.gain.value = 1.0;
      bed.connect(wobble);

      // Highshelf so the WHOLE crowd bus sits "behind" gameplay one-shots —
      // kicks/whistle/goal-swish connect straight to masterGain and never
      // pass through this shelf.
      const shelf = ctx.createBiquadFilter();
      shelf.type = 'highshelf';
      shelf.frequency.value = CROWD_SHELF_HZ;
      shelf.gain.value = CROWD_SHELF_DB;
      wobble.connect(shelf).connect(this.masterGain);

      // ── Rumble: deep, near-mono, "never fully settles" (brown-ish noise
      //    via a steep lowpass cascade well under the wash layer). ───────────
      const rumbleSrc = ctx.createBufferSource();
      rumbleSrc.buffer = this.noise.rumble;
      rumbleSrc.loop = true;
      const rumbleLp1 = ctx.createBiquadFilter();
      rumbleLp1.type = 'lowpass';
      rumbleLp1.frequency.value = 220;
      const rumbleLp2 = ctx.createBiquadFilter();
      rumbleLp2.type = 'lowpass';
      rumbleLp2.frequency.value = 140;
      const rumbleGain = ctx.createGain();
      rumbleGain.gain.value = RUMBLE_GAIN;
      rumbleSrc.connect(rumbleLp1).connect(rumbleLp2).connect(rumbleGain).connect(bed);

      // ── Wash: cascaded-lowpass noise (cheap pink/brown-ish texture), split
      //    into two decorrelated buffers panned ±0.4 for stereo width. ───────
      const washSrcL = ctx.createBufferSource();
      washSrcL.buffer = this.noise.washL;
      washSrcL.loop = true;
      const washLp1L = ctx.createBiquadFilter();
      washLp1L.type = 'lowpass';
      washLp1L.frequency.value = 900;
      const washLp2L = ctx.createBiquadFilter();
      washLp2L.type = 'lowpass';
      washLp2L.frequency.value = 480;
      const washGainL = ctx.createGain();
      washGainL.gain.value = WASH_GAIN_EACH;
      const washPanL = ctx.createStereoPanner();
      washPanL.pan.value = -0.4;
      washSrcL.connect(washLp1L).connect(washLp2L).connect(washGainL).connect(washPanL).connect(bed);

      const washSrcR = ctx.createBufferSource();
      washSrcR.buffer = this.noise.washR;
      washSrcR.loop = true;
      const washLp1R = ctx.createBiquadFilter();
      washLp1R.type = 'lowpass';
      washLp1R.frequency.value = 900;
      const washLp2R = ctx.createBiquadFilter();
      washLp2R.type = 'lowpass';
      washLp2R.frequency.value = 480;
      const washGainR = ctx.createGain();
      washGainR.gain.value = WASH_GAIN_EACH;
      const washPanR = ctx.createStereoPanner();
      washPanR.pan.value = 0.4;
      washSrcR.connect(washLp1R).connect(washLp2R).connect(washGainR).connect(washPanR).connect(bed);

      // ── Voices: bandpassed "voice wash", also split & panned ±0.4. ─────────
      const voiceSrcL = ctx.createBufferSource();
      voiceSrcL.buffer = this.noise.voiceL;
      voiceSrcL.loop = true;
      const bpL = ctx.createBiquadFilter();
      bpL.type = 'bandpass';
      bpL.frequency.value = 550;
      bpL.Q.value = 0.7;
      const voiceGainL = ctx.createGain();
      voiceGainL.gain.value = VOICE_GAIN_EACH;
      const voicePanL = ctx.createStereoPanner();
      voicePanL.pan.value = -0.4;
      voiceSrcL.connect(bpL).connect(voiceGainL).connect(voicePanL).connect(bed);

      const voiceSrcR = ctx.createBufferSource();
      voiceSrcR.buffer = this.noise.voiceR;
      voiceSrcR.loop = true;
      const bpR = ctx.createBiquadFilter();
      bpR.type = 'bandpass';
      bpR.frequency.value = 550;
      bpR.Q.value = 0.7;
      const voiceGainR = ctx.createGain();
      voiceGainR.gain.value = VOICE_GAIN_EACH;
      const voicePanR = ctx.createStereoPanner();
      voicePanR.pan.value = 0.4;
      voiceSrcR.connect(bpR).connect(voiceGainR).connect(voicePanR).connect(bed);

      // ── Irregular swell: two INCOMMENSURATE-rate LFOs feed the wobble gain
      //    (summed there with crowdTick()'s random walk) — never a loop. ─────
      const lfoA = ctx.createOscillator();
      lfoA.type = 'sine';
      lfoA.frequency.value = 0.043;
      const lfoAGain = ctx.createGain();
      lfoAGain.gain.value = 0.05;
      lfoA.connect(lfoAGain).connect(wobble.gain);

      const lfoB = ctx.createOscillator();
      lfoB.type = 'sine';
      lfoB.frequency.value = 0.11;
      const lfoBGain = ctx.createGain();
      lfoBGain.gain.value = 0.04;
      lfoB.connect(lfoBGain).connect(wobble.gain);

      rumbleSrc.start();
      washSrcL.start();
      washSrcR.start();
      voiceSrcL.start();
      voiceSrcR.start();
      lfoA.start();
      lfoB.start();

      this.crowdBedGainNode = bed;
      this.crowdWobbleGainNode = wobble;
      this.crowdNodes.push(
        rumbleSrc, rumbleLp1, rumbleLp2, rumbleGain,
        washSrcL, washLp1L, washLp2L, washGainL, washPanL,
        washSrcR, washLp1R, washLp2R, washGainR, washPanR,
        voiceSrcL, bpL, voiceGainL, voicePanL,
        voiceSrcR, bpR, voiceGainR, voicePanR,
        lfoA, lfoAGain, lfoB, lfoBGain,
        wobble, shelf, bed,
      );
      this.crowdBuilt = true;
    } catch {
      this.crowdBuilt = false;
    }
  }

  /** A localized "surge" one-shot — a chant/reaction reads as a bandpass
   *  sweep across a fresh noise source, gated in and back out over its
   *  2-4s duration and mixed into the crowd bus itself (so it inherits the
   *  bed's own level/highshelf/wobble rather than fighting them). Never
   *  throws; a no-op if the crowd bus hasn't been built (crowd: false, or a
   *  failed/missing AudioContext). */
  playCrowdSurge(durationS: number): void {
    try {
      this.ensureCrowd();
      const ctx = this.ctx;
      if (!ctx || !this.crowdBedGainNode || !this.noise) return;
      const t0 = ctx.currentTime;
      const dur = Math.max(0.5, durationS);
      const src = ctx.createBufferSource();
      src.buffer = this.noise.surge;
      src.loop = true;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 1.1;
      const startHz = 380 + Math.random() * 140;
      const endHz = 900 + Math.random() * 350;
      bp.frequency.setValueAtTime(startHz, t0);
      bp.frequency.linearRampToValueAtTime(endHz, t0 + dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(SURGE_PEAK_GAIN, t0 + dur * 0.35);
      g.gain.setTargetAtTime(0.0001, t0 + dur * 0.5, dur * 0.3);
      src.connect(bp).connect(g).connect(this.crowdBedGainNode);
      src.start(t0);
      src.stop(t0 + dur + 0.1);
      src.onended = (): void => { try { src.disconnect(); bp.disconnect(); g.disconnect(); } catch { /* ignore */ } };
    } catch {
      /* ignore */
    }
  }

  /** Continuous — called every tick with the decided bed gain (0..1). */
  setCrowdBedGain(gain: number): void {
    try {
      this.ensureCrowd();
      if (!this.crowdBedGainNode || !this.ctx) return;
      this.crowdBedGainNode.gain.setTargetAtTime(clamp01(gain), this.now(), 0.08);
    } catch {
      /* ignore */
    }
  }

  /** Short low thump + a filtered-noise click transient. Strength 0..1. */
  playKick(strength: number, powerful: boolean): void {
    try {
      const ctx = this.ensureContext();
      if (!ctx || !this.masterGain) return;
      const t0 = ctx.currentTime;
      const baseFreq = powerful ? 95 : 130 - strength * 25;
      const dur = powerful ? 0.24 : 0.11 + strength * 0.03;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(Math.max(60, baseFreq), t0);
      osc.frequency.exponentialRampToValueAtTime(Math.max(40, baseFreq * 0.6), t0 + dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.35 + strength * 0.5, t0 + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      osc.connect(g).connect(this.masterGain);
      osc.start(t0);
      osc.stop(t0 + dur + 0.02);
      osc.onended = (): void => { try { osc.disconnect(); g.disconnect(); } catch { /* ignore */ } };

      if (this.noise) {
        const click = ctx.createBufferSource();
        click.buffer = this.noise.click;
        const hp = ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 2200;
        const cg = ctx.createGain();
        cg.gain.setValueAtTime(0.18 + strength * 0.3, t0);
        cg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.035);
        click.connect(hp).connect(cg).connect(this.masterGain);
        click.start(t0);
        click.stop(t0 + 0.05);
        click.onended = (): void => { try { click.disconnect(); hp.disconnect(); cg.disconnect(); } catch { /* ignore */ } };
      }
    } catch {
      /* ignore */
    }
  }

  /** Soft low thud — a ball bounce. Strength 0..1. */
  playBounce(strength: number): void {
    try {
      const ctx = this.ensureContext();
      if (!ctx || !this.masterGain) return;
      const t0 = ctx.currentTime;
      const dur = 0.09;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(100 - strength * 15, t0);
      osc.frequency.exponentialRampToValueAtTime(60, t0 + dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.12 + strength * 0.22, t0 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      osc.connect(g).connect(this.masterGain);
      osc.start(t0);
      osc.stop(t0 + dur + 0.02);
      osc.onended = (): void => { try { osc.disconnect(); g.disconnect(); } catch { /* ignore */ } };
    } catch {
      /* ignore */
    }
  }

  /** Highpassed noise burst — the net "swish" on a goal. */
  playGoalSwish(): void {
    try {
      const ctx = this.ensureContext();
      if (!ctx || !this.masterGain || !this.noise) return;
      const t0 = ctx.currentTime;
      const src = ctx.createBufferSource();
      src.buffer = this.noise.long;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 4200;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.4, t0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.26);
      src.connect(hp).connect(g).connect(this.masterGain);
      src.start(t0);
      src.stop(t0 + 0.3);
      src.onended = (): void => { try { src.disconnect(); hp.disconnect(); g.disconnect(); } catch { /* ignore */ } };
    } catch {
      /* ignore */
    }
  }

  /** Downward pitch-bent filtered noise burst — the crowd's disappointed "ohh". */
  playOhh(): void {
    try {
      const ctx = this.ensureContext();
      if (!ctx || !this.masterGain || !this.noise) return;
      const t0 = ctx.currentTime;
      const src = ctx.createBufferSource();
      src.buffer = this.noise.long;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.Q.value = 0.9;
      bp.frequency.setValueAtTime(1200, t0);
      bp.frequency.exponentialRampToValueAtTime(280, t0 + 0.7);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.32, t0 + 0.04);
      g.gain.setValueAtTime(0.32, t0 + 0.3);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.9);
      src.connect(bp).connect(g).connect(this.masterGain);
      src.start(t0);
      src.stop(t0 + 1.0);
      src.onended = (): void => { try { src.disconnect(); bp.disconnect(); g.disconnect(); } catch { /* ignore */ } };
    } catch {
      /* ignore */
    }
  }

  /** One referee-whistle blast: two detuned oscillators through a bandpass
   *  with a fast tremolo — the classic pea-whistle warble. */
  private blast(startAt: number, durationS: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.masterGain) return;
    const osc1 = ctx.createOscillator();
    osc1.type = 'square';
    osc1.frequency.value = 2100;
    const osc2 = ctx.createOscillator();
    osc2.type = 'sawtooth';
    osc2.frequency.value = 2300;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2200;
    bp.Q.value = 5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, startAt);
    g.gain.exponentialRampToValueAtTime(0.5, startAt + 0.02);
    g.gain.setValueAtTime(0.5, Math.max(startAt + 0.02, startAt + durationS - 0.03));
    g.gain.exponentialRampToValueAtTime(0.0001, startAt + durationS);
    // ~35Hz tremolo — modulates ADDITIVELY on top of the gain envelope above
    // (Web Audio sums every input feeding an AudioParam).
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 35;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.32;
    lfo.connect(lfoGain).connect(g.gain);

    osc1.connect(bp);
    osc2.connect(bp);
    bp.connect(g).connect(this.masterGain);
    const stopAt = startAt + durationS + 0.02;
    osc1.start(startAt); osc2.start(startAt); lfo.start(startAt);
    osc1.stop(stopAt); osc2.stop(stopAt); lfo.stop(stopAt);
    const cleanup = (): void => {
      try { osc1.disconnect(); osc2.disconnect(); bp.disconnect(); g.disconnect(); lfo.disconnect(); lfoGain.disconnect(); } catch { /* ignore */ }
    };
    osc2.onended = cleanup;
  }

  playWhistle(pattern: WhistlePattern): void {
    if (pattern === 'none') return;
    try {
      const ctx = this.ensureContext();
      if (!ctx) return;
      const t0 = ctx.currentTime;
      if (pattern === 'short') this.blast(t0, 0.16);
      else if (pattern === 'long') this.blast(t0, 0.6);
      else if (pattern === 'triple') {
        this.blast(t0, 0.14);
        this.blast(t0 + 0.22, 0.14);
        this.blast(t0 + 0.44, 0.14);
      }
    } catch {
      /* ignore */
    }
  }

  /** Tiny filtered tick — menu button clicks (optional, §6). */
  playUiClick(): void {
    try {
      const ctx = this.ensureContext();
      if (!ctx || !this.masterGain || !this.noise) return;
      const t0 = ctx.currentTime;
      const src = ctx.createBufferSource();
      src.buffer = this.noise.click;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 2600;
      bp.Q.value = 1.2;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.003);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.03);
      src.connect(bp).connect(g).connect(this.masterGain);
      src.start(t0);
      src.stop(t0 + 0.04);
      src.onended = (): void => { try { src.disconnect(); bp.disconnect(); g.disconnect(); } catch { /* ignore */ } };
    } catch {
      /* ignore */
    }
  }

  dispose(): void {
    try {
      for (const n of this.crowdNodes) {
        try { n.stop?.(); } catch { /* already stopped */ }
        try { n.disconnect(); } catch { /* ignore */ }
      }
      this.crowdNodes.length = 0;
      this.crowdBedGainNode = null;
      this.crowdWobbleGainNode = null;
      this.crowdBuilt = false;
      if (this.ctx && this.ctx.state !== 'closed') void this.ctx.close().catch(() => undefined);
    } catch {
      /* never throw on teardown */
    }
    this.ctx = null;
    this.masterGain = null;
    this.duckGain = null;
  }
}

// ---------------------------------------------------------------------------
// Public factory
// ---------------------------------------------------------------------------

export function createAudioSystem(opts: AudioSystemOptions = {}): AudioSystem {
  const crowdEnabled = opts.crowd ?? true;
  const synth = new SynthEngine();
  const decisionState = createAudioDecisionState();
  const triggers = createAudioTriggers();

  return {
    update(input: AudioTickInput, events: readonly GameEventLike[]): void {
      try {
        decideAudioFrame(decisionState, triggers, input, events);
      } catch {
        return; // the decision layer is pure; never let a bad input crash the loop
      }
      if (crowdEnabled) {
        synth.setCrowdBedGain(triggers.crowdBedGain);
        synth.crowdTick(input.dt);
        if (triggers.crowdSurge) synth.playCrowdSurge(triggers.crowdSurgeDurationS);
      }
      if (triggers.kick) synth.playKick(triggers.kickStrength, triggers.kickPowerful);
      if (triggers.bounce) synth.playBounce(triggers.bounceStrength);
      if (triggers.goalNetSwish) synth.playGoalSwish();
      if (triggers.crowdOhh) synth.playOhh();
      if (triggers.whistle !== 'none') synth.playWhistle(triggers.whistle);
    },
    setMasterVolume(v: number): void {
      synth.setMasterVolume(v);
    },
    setPaused(paused: boolean): void {
      synth.setPaused(paused);
    },
    resume(): void {
      synth.resume();
    },
    dispose(): void {
      synth.dispose();
    },
  };
}

/**
 * Standalone tiny click tick for menu.ts (§6 — "optional, only if trivial").
 * Deliberately separate from `createAudioSystem`: the menu has no RenderState/
 * events to feed a full match audio system, just button clicks.
 */
export interface UiClickSound {
  play(): void;
  resume(): void;
  dispose(): void;
}

export function createUiClickSound(): UiClickSound {
  const synth = new SynthEngine();
  return {
    play(): void { synth.playUiClick(); },
    resume(): void { synth.resume(); },
    dispose(): void { synth.dispose(); },
  };
}
